/**
 * Das Live-Diktat sendet mit 16 kHz statt mit der Rate des Geräts.
 * (24.09.2026, Ticket 2519)
 *
 * ## Woher das kommt
 *
 * Martin, 23.09.: *„Die Transkription rückt sehr langsam nach."* Seit dem
 * 23.09. schreibt jede Aufnahme eine Zeile `[diktat]` mit dem größten Stau im
 * Sendepuffer (`pufferMax`, ws.bufferedAmount) und dem Abstand vom Sprechen
 * bis zum erkannten Wort (`nachlauf`). 25 Aufnahmen bis 24.09. mittags:
 *
 *     pufferMax bis 180 KB         13×   nachlauf 0,7–5,6 s
 *     pufferMax 350 KB bis 1,56 MB  5×   nachlauf 6,7–18,4 s
 *
 * Alle mit `rate=48000`: rohes PCM mit 48 kHz sind 768 kbit/s — unterwegs
 * mehr, als die Leitung trägt; 1,56 MB Stau sind 16 Sekunden Ton, die noch
 * auf dem Gerät warten. Spracherkennung braucht nicht mehr als 16 kHz; das
 * ist ein Drittel der Menge.
 *
 * ## Warum gemittelt statt jeden dritten Wert genommen
 *
 * Nur jeden dritten Wert zu nehmen, faltet alles über 8 kHz zurück ins Band
 * (Zischlaute werden zu Rauschen). Der Mittelwert über das Fenster ist ein
 * einfacher Tiefpass — für Sprache genug, und er kostet fast nichts.
 */

/** Die Rate, mit der das Diktat sendet. */
export const DIKTAT_RATE = 16_000;

/** Mit welcher Rate gesendet wird: nie höher als die Quelle. */
export function senderate(quelle: number): number {
  return quelle > DIKTAT_RATE ? DIKTAT_RATE : Math.round(quelle);
}

function alsInt16(s: number): number {
  const x = Math.max(-1, Math.min(1, s));
  return x < 0 ? x * 0x8000 : x * 0x7fff;
}

/**
 * Taktet einen fortlaufenden Strom von Rahmen herunter. Zustand über die
 * Rahmengrenzen: der Bruchteil der Position und der angefangene Rest — sonst
 * entstünde an jeder Grenze ein Knacken und die Zeitachse liefe davon.
 */
export class Heruntertakter {
  private readonly schritt: number;
  private rest: Float32Array = new Float32Array(0);
  private pos = 0;

  constructor(
    readonly quelle: number,
    readonly ziel: number = senderate(quelle),
  ) {
    this.schritt = quelle / ziel;
  }

  /** Ein Rahmen Float32 (-1…1) hinein, Int16-PCM in der Zielrate heraus. */
  verarbeite(eingang: Float32Array): Int16Array<ArrayBuffer> {
    if (this.schritt <= 1) {
      const pcm = new Int16Array(eingang.length);
      for (let i = 0; i < eingang.length; i++) pcm[i] = alsInt16(eingang[i] ?? 0);
      return pcm;
    }
    const puffer = new Float32Array(this.rest.length + eingang.length);
    puffer.set(this.rest, 0);
    puffer.set(eingang, this.rest.length);
    const aus: number[] = [];
    while (this.pos + this.schritt <= puffer.length) {
      const a = Math.floor(this.pos);
      const b = Math.max(a + 1, Math.floor(this.pos + this.schritt));
      let summe = 0;
      for (let i = a; i < b; i++) summe += puffer[i] ?? 0;
      aus.push(alsInt16(summe / (b - a)));
      this.pos += this.schritt;
    }
    const verbraucht = Math.floor(this.pos);
    this.rest = puffer.slice(verbraucht);
    this.pos -= verbraucht;
    const pcm = new Int16Array(aus.length);
    pcm.set(aus);
    return pcm;
  }
}
